/**
 * useBuildVersion — fetches build metadata from GET /api/version once,
 * caches the result at module level so repeated renders never re-fetch.
 *
 * On error or non-200 response all fields are null.
 * No authentication required — the endpoint is public.
 */
import { useEffect, useState } from 'react';

export interface BuildVersionData {
  version: string | null;
  build: number | null;
  env: string | null;
  ref: string | null;
  sha: string | null;
  timestamp: string | null;
}

const NULL_DATA: BuildVersionData = {
  version: null,
  build: null,
  env: null,
  ref: null,
  sha: null,
  timestamp: null,
};

// Module-level promise cache — only one fetch ever fires per page load.
let _promise: Promise<BuildVersionData> | null = null;

function fetchBuildVersion(): Promise<BuildVersionData> {
  if (_promise !== null) return _promise;
  _promise = (async () => {
    try {
      const res = await fetch('/api/version');
      if (!res.ok) return { ...NULL_DATA };
      const json = (await res.json()) as { success: boolean; data: BuildVersionData };
      if (!json.success || !json.data) return { ...NULL_DATA };
      return json.data;
    } catch {
      return { ...NULL_DATA };
    }
  })();
  return _promise;
}

/**
 * Resets the module-level cache.  Only for use in unit tests.
 */
export function _resetBuildVersionCache(): void {
  _promise = null;
}

export function useBuildVersion(): BuildVersionData {
  const [data, setData] = useState<BuildVersionData>(NULL_DATA);

  useEffect(() => {
    let cancelled = false;
    fetchBuildVersion().then((result) => {
      if (!cancelled) setData(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}
